import type { ReactNode } from "react";
import { BaseButton } from "../../../../../../components/Button/BaseButton.tsx";
import { EmptyState } from "../../../../../../components/EmptyState/EmptyState.tsx";
import {
  CIRCLE_SHAPE_TYPE,
  LINE_LAYER_TYPE,
  RECT_SHAPE_TYPE,
  SHAPE_LAYER_TYPE,
  TRIANGLE_SHAPE_TYPE,
} from "../../Designer/constants.ts";
import { useCreateLayer } from "../../Designer/hooks/useCreateLayer.ts";

export const ShapeEmptyState = () => {
  const createLayer = useCreateLayer();

  return (
    <EmptyState
      iconName="Shapes"
      title="Shape"
      description="Use shapes to create winning layouts."
      className="h-full w-full px-300"
    >
      <div className="flex-row items-center gap-400 flex-wrap">
        <StickerButton
          onClick={() => {
            createLayer({
              layerType: SHAPE_LAYER_TYPE,
              shapeType: RECT_SHAPE_TYPE,
            });
          }}
        >
          <RectangleSVG size={100} />
        </StickerButton>
        <StickerButton
          onClick={() => {
            createLayer({
              layerType: SHAPE_LAYER_TYPE,
              shapeType: CIRCLE_SHAPE_TYPE,
            });
          }}
        >
          <CircleSVG size={100} />
        </StickerButton>
        <StickerButton
          onClick={() => {
            createLayer({
              layerType: SHAPE_LAYER_TYPE,
              shapeType: TRIANGLE_SHAPE_TYPE,
            });
          }}
        >
          <TriangleSVG size={100} />
        </StickerButton>
        <StickerButton
          onClick={() => {
            createLayer({
              layerType: LINE_LAYER_TYPE,
            });
          }}
        >
          <DiagonalLineSVG size={100} />
        </StickerButton>
      </div>
    </EmptyState>
  );
};

const StickerButton = ({
  onClick,
  children,
}: {
  onClick?: () => void;
  children?: ReactNode;
}) => (
  <BaseButton
    className="h-[100px] aspect-square rounded-300 overflow-hidden hover:opacity-50 bg-input-surface-rest"
    onClick={onClick}
  >
    {children}
  </BaseButton>
);

const RectangleSVG = ({ size }: { size: number }) => (
  <svg width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <rect
      x={size * 0.15}
      y={size * 0.15}
      width={size * 0.7}
      height={size * 0.7}
      fill="black"
      stroke="none"
    />
  </svg>
);

const CircleSVG = ({ size }: { size: number }) => (
  <svg width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <circle
      r={size * 0.35}
      cx={size * 0.5}
      cy={size * 0.5}
      fill="black"
      stroke="none"
    />
  </svg>
);

const TriangleSVG = ({ size }: { size: number }) => (
  <svg width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <polygon
      points={`${size * 0.5},${size * 0.15} ${size * 0.85},${size * 0.85} ${
        size * 0.15
      },${size * 0.85}`}
      fill="black"
      stroke="none"
    />
  </svg>
);

const DiagonalLineSVG = ({ size }: { size: number }) => (
  <svg width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <line
      x1={size * 0.15}
      y1={size * 0.85}
      x2={size * 0.85}
      y2={size * 0.15}
      stroke="black"
      strokeWidth="1.25"
    />
  </svg>
);
